import { Link } from 'react-router-dom';
import React, { useRef, useEffect, useState, useImperativeHandle, forwardRef } from 'react';
import { exportaLlencPDF } from '../exportaPDF';
import { detectaBucles } from '../buclesCausals';
import { reconeix } from '../reconeixArquetips';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { estatInicial, avanca } from '../motorBucles';
import { useTranslation } from 'react-i18next';
import { LLENC, POLARITAT, PER_DEFECTE, colorPerIndex, tracPerIndex } from '../tema';
import { useSelector, useDispatch } from 'react-redux';
import { updateCurrentModelLocally } from '../store/modelSlice';
import * as d3 from 'd3';
import HelpModal from './HelpModal';

const CausalLoopCanvas = forwardRef(({ modelId, onSelectComponent, selectedComponent, onModeChange }, ref) => {
  const { t } = useTranslation();
  const svgRef = useRef(null);
  const dispatch = useDispatch();
  const { currentModel } = useSelector(state => state.models);
  const [variables, setVariables] = useState([]);
  const [relationships, setRelationships] = useState([]);
  const [feedbackLoops, setFeedbackLoops] = useState([]);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  // Canviar el nom d'una variable és el 80 % del que es fa amb el panell de propietats. Amb doble
  // clic surt un camp damunt del node mateix i no cal obrir res.
  const [editant, setEditant] = useState(null);   // { id, valor }
  const [mode, setMode] = useState('select'); // 'select', 'variable', 'link', 'delete', 'pan'
  // TORNAR A TRIAR DESPRÉS D'AFEGIR.
  //
  // Deixar l'eina d'afegir posada després de posar una cosa és el que fa que **acabis omplint el
  // llenç sense voler**: poses un node, mires el que has fet, hi vas a tocar amb el ratolí, i n'hi
  // has posat un altre. La intuïció després de col·locar alguna cosa és **mirar-se-la**, no
  // col·locar-ne una altra.
  //
  // Val per a les peces i per a les fletxes acabades. **NO per a esborrar** —qui esborra sol
  // esborrar-ne unes quantes— ni per a moure el llenç ni per al llapis, que són continus per
  // naturalesa.
  //
  // I s'ha de dir A LA BARRA, que és a fora: fins avui el llenç causal ja hi tornava per dins i
  // el botó es quedava encès, de manera que la barra i el llenç deien coses diferents.
  const tornaATriar = () => { setMode('select'); if (onModeChange) onModeChange('select'); };
  const [linkStart, setLinkStart] = useState(null);
  const [nextId, setNextId] = useState(1);
  const [highlightedLoop, setHighlightedLoop] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  
  // Simulation state
  const [isSimulating, setIsSimulating] = useState(false);
  const [simulationTime, setSimulationTime] = useState(0);
  const [variableValues, setVariableValues] = useState({});
  const [flowParticles, setFlowParticles] = useState([]);
  const [simulationSettings, setSimulationSettings] = useState({
    speed: 1,
    showValues: true,
    showParticles: true,
  });
  const animationFrameRef = useRef(null);
  const isSimulatingRef = useRef(false);
  const simulationSettingsRef = useRef(simulationSettings);
  // El bucle d'animació es crea un sol cop i tancaria `variables` i `relationships` de la volta
  // en què va arrencar: afegir un lligam o canviar un valor mentre corre no comptaria. Amb refs,
  // el motor sempre rep el dibuix d'ara.
  const variablesRef = useRef(variables);
  const relationshipsRef = useRef(relationships);
  // Els valors, també: `generateFlowParticles` es crida des del bucle d'animació, que tanca
  // l'estat de quan va arrencar. Sense això les partícules es generaven amb els valors inicials
  // per sempre, encara que el model ja fos en un altre lloc.
  const valorsRef = useRef({});
  // La sèrie temporal: què ha fet cada variable des que va arrencar. El nivell del node diu on és
  // ara; això diu **d'on ve**, que és l'altra meitat de «què creix i què decreix».
  const [serie, setSerie] = useState([]);
  const [veureGrafic, setVeureGrafic] = useState(true);
  // On és el gràfic. `null` vol dir «ancorat a baix a la dreta»; en arrossegar-lo passa a tenir
  // coordenades pròpies. Es pot posar on no tapi el que estàs mirant.
  const [posGrafic, setPosGrafic] = useState(null);
  // Plegat per defecte: mentre dibuixes, la llista tapa la cantonada del llenç.
  const [veureBucles, setVeureBucles] = useState(false);
  const arrossegant = useRef(null);
  const reforcadors = feedbackLoops.filter((b) => b.type === 'reinforcing').length;
  const equilibradors = feedbackLoops.length - reforcadors;

  // S'ASSEMBLA A ALGUN ARQUETIP?
  //
  // Es calcula del dibuix, com els bucles, i **no és cap etiqueta: és una pista**. L'empremta
  // mira la forma —quins bucles hi ha, com de llargs, i com se solapen— i no els noms ni el que
  // vol dir cada variable, o sigui que dues estructures poden encaixar i voler dir coses
  // diferents. Per això es diu «s'assembla a» i **no hi ha cap número de confiança**: un
  // percentatge inventat només serviria per fer semblar exacte el que no ho és.
  const arquetipsTrobats = React.useMemo(
    () => reconeix(variables, relationships), [variables, relationships]);
  const ultimaMostra = useRef(-1);
  const interval = useRef(0.2);
  
  // Keep refs in sync with state
  useEffect(() => {
    isSimulatingRef.current = isSimulating;
  }, [isSimulating]);
  
  useEffect(() => {
    simulationSettingsRef.current = simulationSettings;
  }, [simulationSettings]);

  useEffect(() => { variablesRef.current = variables; }, [variables]);
  useEffect(() => { relationshipsRef.current = relationships; }, [relationships]);
  useEffect(() => { valorsRef.current = variableValues; }, [variableValues]);

  // ELS BUCLES ES CALCULEN A PARTIR DEL DIBUIX, no de les accions.
  //
  // Abans es cridava la detecció des de cada lloc que afegia o esborrava un lligam. Amb això,
  // **obrir un diagrama ja desat no en trobava cap**: no s'havia afegit res en aquesta sessió i
  // la funció no s'havia cridat mai. Com que els bucles són una funció pura del dibuix, el lloc
  // on han de viure és aquí.
  useEffect(() => {
    setFeedbackLoops(detectaBucles(variables, relationships));
  }, [variables, relationships]);

  // Arrossegar el gràfic. Els oients van al document i no al quadre, o deixar-lo anar fora de la
  // capçalera el deixaria enganxat al ratolí.
  useEffect(() => {
    const mou = (e) => {
      if (!arrossegant.current) return;
      const a = arrossegant.current;
      // Amb topalls: sense això es podia arrossegar fora del llenç i quedava on ja no es pot
      // tornar a agafar. Se'n deixa sortir una mica, però la capçalera sempre queda a l'abast.
      const acota = (v, min, max) => Math.max(min, Math.min(max, v));
      setPosGrafic({
        x: acota(e.clientX - a.pareX - a.dx, 0, Math.max(0, a.pareW - 80)),
        y: acota(e.clientY - a.pareY - a.dy, 0, Math.max(0, a.pareH - 28)),
      });
    };
    const solta = () => { arrossegant.current = null; };
    document.addEventListener('mousemove', mou);
    document.addEventListener('mouseup', solta);
    return () => {
      document.removeEventListener('mousemove', mou);
      document.removeEventListener('mouseup', solta);
    };
  }, []);

  // Load model data when currentModel changes
  useEffect(() => {
    if (currentModel && currentModel.causalLoopModel) {
      setVariables(currentModel.causalLoopModel.variables || []);
      setRelationships(currentModel.causalLoopModel.relationships || []);
      setFeedbackLoops(currentModel.causalLoopModel.loops || []);
      
      const maxId = Math.max(0, ...(currentModel.causalLoopModel.variables?.map(v => parseInt(v.id) || 0) || [0]));
      setNextId(maxId + 1);
    }
  }, [currentModel]);

  // Expose update functions via ref
  useImperativeHandle(ref, () => ({
    updateComponent: (updates) => {
      if (updates.type === 'variable') {
        updateVariable(updates.id, updates);
      } else if (updates.type === 'relationship' || updates.id?.startsWith('rel_')) {
        updateRelationship(updates.id, updates);
      }
    },
    startSimulation: (settings) => {
      if (settings) {
        setSimulationSettings(settings);
      }
      setIsSimulating(true);
      setSimulationTime(0);
      setSerie([]);
      ultimaMostra.current = -1;
      interval.current = 0.2;
      initializeVariableValues();
      startAnimationLoop();
    },
    stopSimulation: () => {
      setIsSimulating(false);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
      setFlowParticles([]); // Clear particles when stopping
    },
    resetSimulation: () => {
      setIsSimulating(false);
      setSimulationTime(0);
      // Reiniciar sí que el buida: és el que vol dir reiniciar.
      setSerie([]);
      ultimaMostra.current = -1;
      interval.current = 0.2;
      initializeVariableValues();
      setFlowParticles([]);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    },
    updateSettings: (settings) => {
      setSimulationSettings(settings);
    },
    obreAjuda: () => setShowHelp(true),
    exportaPDF: (titol, peu) => exportaLlencPDF(svgRef.current, titol, peu),
    setMode: (newMode) => {
      setMode(newMode);
      setLinkStart(null); // Reset link start when changing modes
    }
  }), [variables, relationships]);

  // L'estat del motor viu en un ref i no a l'estat de React: canvia seixanta cops per segon i
  // el que ha de repintar són només els valors, no la història ni l'acumulador de temps.
  const estatMotor = useRef(null);

  const initializeVariableValues = () => {
    estatMotor.current = estatInicial(variables);
    setVariableValues({ ...estatMotor.current.valors });
  };

  // El bucle d'animació.
  //
  // El que mana és **quant temps real ha passat**, no quants fotogrames s'han dibuixat. Abans
  // s'aplicava un canvi sencer per fotograma, o sigui que en una pantalla de 120 Hz el model
  // corria el doble de ràpid que en una de 60. Ara el motor rep els segons transcorreguts i ell
  // decideix quantes passes de `DT` hi caben.
  const startAnimationLoop = () => {
    let anterior = null;

    const animate = (timestamp) => {
      if (!isSimulatingRef.current) return;

      if (anterior === null) anterior = timestamp;
      const segons = Math.min(0.25, (timestamp - anterior) / 1000);  // un salt gros no es recupera de cop
      anterior = timestamp;

      if (estatMotor.current) {
        estatMotor.current = avanca(
          estatMotor.current, variablesRef.current, relationshipsRef.current,
          segons, simulationSettingsRef.current.speed
        );
        setVariableValues({ ...estatMotor.current.valors });
        setSimulationTime(estatMotor.current.temps);

        // Una mostra cada 0,2 unitats de temps de model: prou densa per veure la forma de la
        // corba i prou espaiada perquè dibuixar-la no competeixi amb la simulació.
        const t = estatMotor.current.temps;
        if (t - ultimaMostra.current >= interval.current) {
          ultimaMostra.current = t;
          setSerie((prev) => {
            const punt = { t: Number(t.toFixed(2)), ...estatMotor.current.valors };
            const seguent = [...prev, punt];
            // Quan s'omple, es queda un punt de cada dos i es dobla l'interval de mostreig.
            // Així el gràfic **no perd mai el principi** —que és el tros interessant—, només hi
            // perd resolució. Tallar la cua feia desaparèixer l'arrencada sense avisar.
            if (seguent.length > 400) {
              interval.current *= 2;
              return seguent.filter((_, i) => i % 2 === 0);
            }
            return seguent;
          });
        }
        if (simulationSettingsRef.current.showParticles) {
          generateFlowParticles();
          // Les crea `generate` i les fa avançar `update`. El bucle nou es va deixar la segona:
          // les partícules naixien i es quedaven quietes damunt de la fletxa.
          updateFlowParticles();
        }
        highlightMostActiveLoop(estatMotor.current.valors);
      }

      animationFrameRef.current = requestAnimationFrame(animate);
    };

    animationFrameRef.current = requestAnimationFrame(animate);
  };

  // Update simulation logic
  // El càlcul que hi havia aquí —influència, esmorteïment i una atracció de tothom cap a 50,
  // aplicats un cop per fotograma— ha passat a `src/motorBucles.js`, que es pot provar sol.
  // Vegeu `frontend/prova-motor.js`.

  // Highlight the most active feedback loop during simulation
  const highlightMostActiveLoop = (currentValues) => {
    if (feedbackLoops.length === 0) return;
    
    let mostActiveLoop = null;
    let maxActivity = 0;
    
    feedbackLoops.forEach(loop => {
      // Calculate loop activity based on variable values and changes
      const activity = loop.path.reduce((sum, variableId) => {
        const value = currentValues[variableId] || 0;
        return sum + value;
      }, 0) / loop.path.length;
      
      if (activity > maxActivity) {
        maxActivity = activity;
        mostActiveLoop = loop;
      }
    });
    
    // Only highlight if activity is significant and different from current
    if (maxActivity > 30 && mostActiveLoop?.id !== highlightedLoop?.id) {
      setHighlightedLoop(mostActiveLoop);
    }
  };

  // Generate flow particles along relationships based on actual influence
  const generateFlowParticles = () => {
    const valors = valorsRef.current;
    const vars = variablesRef.current;
    relationshipsRef.current.forEach(rel => {
      const sourceValue = valors[rel.source] || 0;
      const targetValue = valors[rel.target] || 0;
      
      // Calculate influence strength
      const normalizedSource = sourceValue / 100;
      const influence = rel.polarity === '+' ? 1.5 : -1.5;
      const strength = rel.strength === 'strong' ? 1.5 : rel.strength === 'weak' ? 0.5 : 1;
      const dampening = rel.polarity === '+' ? (1 - targetValue / 100) : (targetValue / 100);
      const totalInfluence = Math.abs(normalizedSource * influence * strength * dampening);
      
      // Generate particle based on influence strength
      if (totalInfluence > 0.1 && Math.random() < totalInfluence * 0.5) {
        const sourceVar = vars.find(v => v.id === rel.source);
        const targetVar = vars.find(v => v.id === rel.target);
        
        if (sourceVar && targetVar) {
          const c = corbaLligam(rel, getVariableCenter(sourceVar), getVariableCenter(targetVar));
          if (!c) return;
          const newParticle = {
            id: Date.now() + Math.random(),
            relationshipId: rel.id,
            progress: 0,
            // Els tres punts de la corba: la partícula ha de seguir la MATEIXA línia que es
            // dibuixa, no la recta entre els dos nodes.
            p0: c.p0, pc: c.c, p1: c.p1,
            polarity: rel.polarity,
            strength: rel.strength || 'medium',
            influenceStrength: totalInfluence
          };
          
          setFlowParticles(prev => [...prev.slice(-15), newParticle]); // Keep max 15 particles
        }
      }
    });
  };

  // Update flow particles
  const updateFlowParticles = () => {
    setFlowParticles(prev => 
      prev.map(particle => ({
        ...particle,
        progress: particle.progress + 0.03
      })).filter(particle => particle.progress <= 1)
    );
  };

  useEffect(() => {
    const svg = d3.select(svgRef.current);
    
    const zoomBehavior = d3.zoom()
      .scaleExtent([0.5, 3])
      .on('zoom', (event) => {
        // Only allow zoom/pan in pan mode or with ctrl/cmd key
        if (mode === 'pan' || event.sourceEvent?.ctrlKey || event.sourceEvent?.metaKey) {
          setZoom(event.transform.k);
          setPan({ x: event.transform.x, y: event.transform.y });
        }
      })
      .filter((event) => {
        // Allow zoom/pan only in pan mode or with special keys
        return mode === 'pan' || event.ctrlKey || event.metaKey || event.type === 'wheel';
      });
    
    svg.call(zoomBehavior);
    
    // Update zoom behavior when mode changes
    if (mode === 'pan') {
      svg.style('cursor', 'move');
    } else {
      svg.style('cursor', null);
    }
  }, [mode]);

  // Es passen els valors nous, no es llegeixen de l'estat.
  //
  // Abans no rebia res i agafava `variables` i `relationships` del tancament, que en el moment
  // de cridar-la **encara són els d'abans del canvi** —`setVariables` no és immediat—. Com que
  // just després l'efecte de dalt torna a copiar l'estat des de Redux, el canvi es desfeia sol:
  // per això **una variable no es podia moure**, tornava on era.
  const updateModelData = (vars = variables, rels = relationships, bucles = feedbackLoops) => {
    dispatch(updateCurrentModelLocally({
      causalLoopModel: {
        ...currentModel.causalLoopModel,
        variables: vars,
        relationships: rels,
        loops: bucles
      }
    }));
  };

  const addVariable = (x, y, tool) => {
    const newVariable = {
      id: nextId.toString(),
      name: tool.name || `${t('tools.variable')} ${nextId}`,
      x: x - 40,
      y: y - 30,
      type: 'variable',
      polarity: '+',
      description: '',
      properties: { 
        initialValue: 50,
        // Cada variable nova agafa el color següent de la paleta. Abans totes sortien iguals i
        // al gràfic quedaven totes les línies del mateix color, que és el problema de debò.
        color: colorPerIndex(variables.length)
      }
    };

    const updatedVariables = [...variables, newVariable];
    setVariables(updatedVariables);
    setNextId(nextId + 1);
    
    // Update model immediately
    dispatch(updateCurrentModelLocally({
      causalLoopModel: {
        ...currentModel.causalLoopModel,
        variables: updatedVariables,
        relationships,
        loops: feedbackLoops
      }
    }));
    
    // Select the new variable for editing
    onSelectComponent(newVariable);
  };

  const deleteVariable = (variableId) => {
    // Remove variable
    const updatedVariables = variables.filter(v => v.id !== variableId);
    // Remove related relationships
    const updatedRelationships = relationships.filter(
      r => r.source !== variableId && r.target !== variableId
    );
    
    setVariables(updatedVariables);
    setRelationships(updatedRelationships);
    
    // Re-detect loops
    const newLoops = detectFeedbackLoops(updatedVariables, updatedRelationships);
    
    dispatch(updateCurrentModelLocally({
      causalLoopModel: {
        ...currentModel.causalLoopModel,
        variables: updatedVariables,
        relationships: updatedRelationships,
        loops: newLoops
      }
    }));
    
    if (selectedComponent?.id === variableId) {
      onSelectComponent(null);
    }
  };

  const updateVariable = (variableId, updates) => {
    const updatedVariables = variables.map(v => 
      v.id === variableId ? { ...v, ...updates } : v
    );
    setVariables(updatedVariables);
    
    dispatch(updateCurrentModelLocally({
      causalLoopModel: {
        ...currentModel.causalLoopModel,
        variables: updatedVariables,
        relationships,
        loops: feedbackLoops
      }
    }));
  };

  const addRelationship = (sourceId, targetId, polarity = '+') => {
    // Només es bloqueja el MATEIX lligam, no el de tornada.
    //
    // Abans es comprovaven les dues direccions alhora, o sigui que un cop feta A→B ja no es podia
    // fer B→A. I aquell parell **és el bucle causal més petit que existeix** —el primer exemple
    // que s'ensenya—, de manera que la comprovació de duplicats impedia justament el que
    // l'aplicació serveix per dibuixar.
    const exists = relationships.some(
      r => r.source === sourceId && r.target === targetId
    );
    if (exists) return;
    // Un lligam d'una variable a ella mateixa tampoc no vol dir res en un diagrama causal.
    if (sourceId === targetId) return;

    const newRelationship = {
      id: `rel_${Date.now()}`,
      type: 'relationship',
      source: sourceId,
      target: targetId,
      polarity,
      delay: 0,
      strength: 'medium',
      name: '',
      description: ''
    };

    const updatedRelationships = [...relationships, newRelationship];
    setRelationships(updatedRelationships);
    
    // Update model and detect loops
    const newLoops = detectFeedbackLoops(variables, updatedRelationships);
    
    dispatch(updateCurrentModelLocally({
      causalLoopModel: {
        ...currentModel.causalLoopModel,
        variables,
        relationships: updatedRelationships,
        loops: newLoops
      }
    }));
  };

  const deleteRelationship = (relationshipId) => {
    const updatedRelationships = relationships.filter(r => r.id !== relationshipId);
    setRelationships(updatedRelationships);
    
    const newLoops = detectFeedbackLoops(variables, updatedRelationships);
    
    dispatch(updateCurrentModelLocally({
      causalLoopModel: {
        ...currentModel.causalLoopModel,
        variables,
        relationships: updatedRelationships,
        loops: newLoops
      }
    }));
    
    if (selectedComponent?.id === relationshipId) {
      onSelectComponent(null);
    }
  };

  const updateRelationship = (relationshipId, updates) => {
    const updatedRelationships = relationships.map(r => 
      r.id === relationshipId ? { ...r, ...updates } : r
    );
    setRelationships(updatedRelationships);
    
    // Re-detect loops if polarity changed
    const newLoops = detectFeedbackLoops(variables, updatedRelationships);
    
    dispatch(updateCurrentModelLocally({
      causalLoopModel: {
        ...currentModel.causalLoopModel,
        variables,
        relationships: updatedRelationships,
        loops: newLoops
      }
    }));
  };

  // La detecció viu a `src/buclesCausals.js`, fora de React i amb proves pròpies
  // (`npm run prova:bucles`). La que hi havia aquí feia servir un `visited` compartit per tota la
  // cerca i **es deixava els bucles que compartissin un node** amb un de trobat abans.
  // Torna els bucles I els desa a l'estat: totes dues coses, que és el que feia la funció vella
  // i el que esperen els cinc llocs que la criden.
  const detectFeedbackLoops = (vars, rels) => {
    const trobats = detectaBucles(vars, rels);
    setFeedbackLoops(trobats);
    return trobats;
  };

  const handleCanvasClick = (e) => {
    if (mode === 'select') return;
    
    const rect = svgRef.current.getBoundingClientRect();
    const x = (e.clientX - rect.left - pan.x) / zoom;
    const y = (e.clientY - rect.top - pan.y) / zoom;

    if (mode === 'variable') {
      // Amb número. Totes es deien «Variable» i el recorregut d'un bucle quedava
      // «Variable → Variable → Variable»: il·legible fins que no les rebategessis una a una.
      addVariable(x, y, { name: `${t('tools.variable')} ${nextId}` });
      tornaATriar();
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    const toolData = e.dataTransfer.getData('tool');
    if (toolData) {
      const tool = JSON.parse(toolData);
      if (tool.id === 'variable') {
        const rect = svgRef.current.getBoundingClientRect();
        const x = (e.clientX - rect.left - pan.x) / zoom;
        const y = (e.clientY - rect.top - pan.y) / zoom;
        addVariable(x, y, tool);
      }
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't handle keys if user is typing in an input field
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }
      
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (selectedComponent) {
          e.preventDefault();
          if (selectedComponent.type === 'variable') {
            deleteVariable(selectedComponent.id);
          } else if (selectedComponent.type === 'relationship'
                     || selectedComponent.id?.startsWith('rel_')) {
            deleteRelationship(selectedComponent.id);
          }
        }
      } else if (e.key === 'Escape') {
        setMode('select');
        setLinkStart(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedComponent]);

  const handleVariableClick = (variable, e) => {
    e.stopPropagation();
    
    if (mode === 'link') {
      if (!linkStart) {
        setLinkStart(variable);
      } else if (linkStart.id !== variable.id) {
        addRelationship(linkStart.id, variable.id, '+');
        setLinkStart(null);
        tornaATriar();
      }
    } else if (mode === 'delete') {
      deleteVariable(variable.id);
      // També després d'esborrar. L'argument és el mateix que en afegir —la intuïció després de
      // treure una cosa és mirar-se el que queda— i aquí les conseqüències són pitjors: quedar-se
      // en mode esborrar i anar tocant vol dir perdre feina.
      tornaATriar();
    } else if (mode === 'select') {
      onSelectComponent(variable);
    }
    // In pan mode, don't do anything on click
  };

  const moveVariable = (variableId, deltaX, deltaY) => {
    const updatedVariables = variables.map(v => 
      v.id === variableId 
        ? { ...v, x: v.x + deltaX, y: v.y + deltaY }
        : v
    );
    setVariables(updatedVariables);
    updateModelData(updatedVariables);
  };

  // LA CORBA D'UN LLIGAM.
  //
  // Amb línies rectes, A→B i B→A es dibuixen l'una damunt de l'altra i el bucle més petit —el
  // primer exemple que s'ensenya— no es veu. Amb una corba de Bézier quadràtica i **la curvatura
  // cap a bandes contràries** segons quin extrem va primer, el parell d'anada i tornada dibuixa
  // un anell tancat, que és la forma que la gent reconeix com a bucle.
  //
  // Tot el que va damunt del lligam —la punta de fletxa, el signe, la marca de retard i les
  // partícules— ha de sortir d'aquí, o cada cosa aniria per la seva banda.
  const corbaLligam = (rel, source, target) => {
    const dx = target.x - source.x;
    const dy = target.y - source.y;
    const dist = Math.hypot(dx, dy);
    if (!dist) return null;

    // La corba sempre cau a la MATEIXA banda respecte del sentit de la fletxa.
    //
    // Sembla que això les faria coincidir, i és al revés: la perpendicular d'A→B i la de B→A ja
    // apunten a bandes contràries, perquè el vector director canvia de signe. Un factor extra per
    // separar-les les tornava a girar i quedaven l'una damunt de l'altra —que és el que passava
    // al primer intent—. Amb un sol sentit, el parell d'anada i tornada dibuixa l'anell.
    const arc = Math.min(60, dist * 0.22);

    // Els extrems s'aparten del centre del node perquè la fletxa no quedi tapada.
    const ux = dx / dist, uy = dy / dist;
    const p0 = { x: source.x + ux * 34, y: source.y + uy * 26 };
    const p1 = { x: target.x - ux * 34, y: target.y - uy * 26 };
    // El punt de control, perpendicular al mig del segment.
    const c = { x: (p0.x + p1.x) / 2 - uy * arc, y: (p0.y + p1.y) / 2 + ux * arc };

    return {
      d: `M ${p0.x} ${p0.y} Q ${c.x} ${c.y} ${p1.x} ${p1.y}`,
      // Un punt qualsevol de la corba, perquè els marcadors hi seguin a sobre.
      punt: (t) => ({
        x: (1 - t) * (1 - t) * p0.x + 2 * (1 - t) * t * c.x + t * t * p1.x,
        y: (1 - t) * (1 - t) * p0.y + 2 * (1 - t) * t * c.y + t * t * p1.y,
      }),
      p0, p1, c,
    };
  };

  const getVariableCenter = (variable) => ({
    x: variable.x + 40,
    y: variable.y + 30
  });

  const renderRelationships = () => {
    return relationships.map(rel => {
      const sourceVar = variables.find(v => v.id === rel.source);
      const targetVar = variables.find(v => v.id === rel.target);
      
      if (!sourceVar || !targetVar) return null;

      const source = getVariableCenter(sourceVar);
      const target = getVariableCenter(targetVar);
      const corba = corbaLligam(rel, source, target);
      if (!corba) return null;

      const isHighlighted = highlightedLoop && 
        highlightedLoop.path.includes(rel.source) && 
        highlightedLoop.path.includes(rel.target);

      // QUANTA CAUSALITAT PASSA ARA PER AQUÍ.
      //
      // Mentre la simulació corre, el gruix del lligam diu quanta influència està entregant de
      // debò: no és el mateix una fletxa que ho mou tot que una que no fa res perquè la seva font
      // està a zero. Amb tots els lligams igual de gruixuts, el dibuix no diu res de què està
      // passant, i era la pregunta «com es veu la causalitat aquí».
      const forcaAra = (() => {
        if (!isSimulating) return null;
        const vFont = variableValues[rel.source];
        const vDesti = variableValues[rel.target];
        if (vFont === undefined || vDesti === undefined) return null;
        const f = rel.strength === 'strong' ? 1.5 : rel.strength === 'weak' ? 0.5 : 1;
        const sat = rel.polarity === '-' ? vDesti / 100 : 1 - vDesti / 100;
        return Math.min(1, (vFont / 100) * f * sat);
      })();
      const gruix = forcaAra === null ? (isHighlighted ? 3 : 2) : 1.5 + forcaAra * 5;

      return (
        <g key={rel.id}>
          {/* El lligam, corbat */}
          <path
            d={corba.d}
            fill="none"
            stroke={isHighlighted ? LLENC.enllacDestacat : LLENC.linia}
            strokeWidth={gruix}
            strokeLinecap="round"
            markerEnd={`url(#arrowhead-${rel.polarity})`}
            className="cursor-pointer transition-[stroke-width] duration-150"
            onClick={() => {
              if (mode === 'delete') {
                deleteRelationship(rel.id);
                tornaATriar();
              } else if (mode === 'select') {
                onSelectComponent(rel);
              }
            }}
          />
          {/* Una franja invisible i ampla damunt de la corba: una línia de dos píxels és molt
              difícil d'encertar amb el ratolí. */}
          <path d={corba.d} fill="none" stroke="transparent" strokeWidth="14"
            className="cursor-pointer"
            onClick={() => {
              if (mode === 'delete') { deleteRelationship(rel.id); tornaATriar(); }
              else if (mode === 'select') onSelectComponent(rel);
            }} />
          
          {/* Polarity indicator */}
          <circle
            cx={corba.punt(0.35).x}
            cy={corba.punt(0.35).y}
            r="8"
            fill={rel.polarity === '+' ? POLARITAT.positiva : POLARITAT.negativa}
            stroke="white"
            strokeWidth="2"
            className="cursor-pointer"
            onClick={(e) => {
              e.stopPropagation();
              // Toggle polarity on click
              updateRelationship(rel.id, { polarity: rel.polarity === '+' ? '-' : '+' });
            }}
          />
          <text
            x={corba.punt(0.35).x}
            y={corba.punt(0.35).y + 4}
            textAnchor="middle"
            fill="white"
            fontSize="10"
            fontWeight="bold"
            className="pointer-events-none select-none"
          >
            {rel.polarity}
          </text>
          
          {/* Delay indicator */}
          {/* EL RETARD, amb la notació de la disciplina: dues ratlles travesseres damunt de la
              fletxa. La caixeta amb «||» que hi havia era una icona inventada. I ara **es pot
              clicar**: era l'única manera d'arribar a editar-lo, i calia encertar la corba.
              Cap a on van les ratlles surt de la tangent de la corba en aquell punt. */}
          {rel.delay > 0 && (() => {
            const a = corba.punt(0.68), b = corba.punt(0.76);
            const ang = Math.atan2(b.y - a.y, b.x - a.x);
            const px = -Math.sin(ang) * 7, py = Math.cos(ang) * 7;
            const c = corba.punt(0.72);
            const dx = Math.cos(ang) * 3.5, dy = Math.sin(ang) * 3.5;
            return (
              <g className="cursor-pointer"
                 onClick={(e) => { e.stopPropagation(); onSelectComponent(rel); }}>
                <line x1={c.x - dx - px} y1={c.y - dy - py} x2={c.x - dx + px} y2={c.y - dy + py}
                  stroke={LLENC.text} strokeWidth="2.5" strokeLinecap="round" />
                <line x1={c.x + dx - px} y1={c.y + dy - py} x2={c.x + dx + px} y2={c.y + dy + py}
                  stroke={LLENC.text} strokeWidth="2.5" strokeLinecap="round" />
                {/* Una taca invisible més ampla, que dues ratlles de 2,5 px no s'encerten. */}
                <circle cx={c.x} cy={c.y} r="12" fill="transparent" />
                <text x={c.x + px * 1.9} y={c.y + py * 1.9 + 4} textAnchor="middle"
                  fill={LLENC.text} stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke"
                  fontSize="10" fontWeight="700" className="pointer-events-none select-none">
                  {rel.delay}
                </text>
              </g>
            );
          })()}

          {/* EL NOM de la relació, si en té. Fins ara totes les fletxes eren mudes i en un
              diagrama gran no se sabia què deia cadascuna. */}
          {rel.name && (
            <text x={corba.punt(0.5).x} y={corba.punt(0.5).y - 10} textAnchor="middle"
              fill={LLENC.textPal} stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke"
              fontSize="10" className="pointer-events-none select-none">
              {rel.name}
            </text>
          )}

        </g>
      );
    });
  };

  // Render flow particles
  const renderFlowParticles = () => {
    return flowParticles.map(particle => {
      // Bézier quadràtica, la mateixa que dibuixa el lligam.
      const t = particle.progress;
      const u = 1 - t;
      const currentPos = {
        x: u * u * particle.p0.x + 2 * u * t * particle.pc.x + t * t * particle.p1.x,
        y: u * u * particle.p0.y + 2 * u * t * particle.pc.y + t * t * particle.p1.y,
      };
      
      // Size based on influence strength
      const baseSize = particle.strength === 'strong' ? 6 : particle.strength === 'weak' ? 3 : 4;
      const influenceSize = Math.max(2, baseSize * (particle.influenceStrength || 1));
      const color = particle.polarity === '+' ? POLARITAT.positiva : POLARITAT.negativa;
      
      // Opacity fades as particle travels
      const opacity = Math.max(0.3, 0.9 - particle.progress * 0.4);
      
      return (
        <g key={particle.id}>
          <circle
            cx={currentPos.x}
            cy={currentPos.y}
            r={influenceSize}
            fill={color}
            opacity={opacity}
          >
            <animate
              attributeName="r"
              values={`${influenceSize * 0.8};${influenceSize * 1.2};${influenceSize * 0.8}`}
              dur="1s"
              repeatCount="indefinite"
            />
          </circle>
          
          {/* Influence trail */}
          <circle
            cx={currentPos.x}
            cy={currentPos.y}
            r={influenceSize * 2}
            fill={color}
            opacity={opacity * 0.2}
          />
        </g>
      );
    });
  };

  // Get cursor style based on mode
  const getCursorStyle = () => {
    switch (mode) {
      case 'pan': return 'cursor-move';
      case 'variable': return 'cursor-cell';
      case 'link': return 'cursor-crosshair';
      case 'delete': return 'cursor-not-allowed';
      default: return 'cursor-default';
    }
  };

  return (
    <div className="flex-1 relative bg-gray-50">
      {/* Help Modal */}
      <HelpModal isOpen={showHelp} onClose={() => setShowHelp(false)} />
      
      {/* El botó d'ajuda ha passat a la barra de dalt: aquí tapava una cantonada del llenç. */}

      {/* EL GRÀFIC. Una línia per variable, amb el temps a l'eix.
          El nivell del node diu on és cadascuna ARA; això diu d'on ve i cap on va, que és el que
          deixa comparar-les entre elles i veure quina mana. */}
      {/* EL GRÀFIC.
          Es queda quan atures: el moment de mirar-lo és justament quan la simulació ha acabat.
          Abans la condició era `isSimulating` i desapareixia en prémer aturar, que és quan més
          falta fa. Ara només marxa en reiniciar o en tornar a engegar. I es pot moure, perquè
          allà on el poso jo sempre tapa alguna cosa d'algú. */}
      {serie.length > 1 && (
        <div
          className={`absolute z-20 bg-white/95 border border-gray-200 rounded-md shadow-lg
                      ${veureGrafic ? 'w-[460px] max-w-[70%]' : ''}`}
          style={posGrafic
            ? { left: posGrafic.x, top: posGrafic.y }
            : { right: 12, bottom: 12 }}
        >
          <div
            onMouseDown={(e) => {
              const caixa = e.currentTarget.parentElement.getBoundingClientRect();
              const pare = e.currentTarget.closest('.relative').getBoundingClientRect();
              arrossegant.current = {
                dx: e.clientX - caixa.left, dy: e.clientY - caixa.top,
                pareX: pare.left, pareY: pare.top,
                pareW: pare.width, pareH: pare.height,
                ampla: caixa.width, alta: caixa.height,
              };
              e.preventDefault();
            }}
            className="flex items-center justify-between gap-3 px-3 py-1.5 text-xs
                       font-semibold text-gray-700 cursor-move select-none border-b border-gray-100"
          >
            <span>{t('sim2.grafic')}{!isSimulating && ` · ${t('sim2.aturada')}`}</span>
            <button type="button" onClick={() => setVeureGrafic(!veureGrafic)}
              className="text-gray-500 hover:text-gray-700 px-1">
              {veureGrafic ? '▾' : '▸'}
            </button>
          </div>
          {veureGrafic && (
            <div className="h-44 pt-1 pr-3 pb-1">
              <ResponsiveContainer width="100%" height="100%">
                {/* Marges de debò: el `left: -18` que hi havia empenyia el gràfic cap enfora i
                    es menjava les etiquetes de l'eix vertical. */}
                <LineChart data={serie} margin={{ top: 4, right: 8, left: 4, bottom: 16 }}>
                  <CartesianGrid stroke={LLENC.reixa} strokeDasharray="2 3" />
                  <XAxis dataKey="t" tick={{ fontSize: 10, fill: LLENC.textPal }}
                    stroke={LLENC.reixa} height={26}
                    label={{ value: t('sim2.eixTemps'), position: 'insideBottom', offset: -4,
                             style: { fontSize: 10, fill: LLENC.textPal } }} />
                  <YAxis domain={[0, 100]} ticks={[0, 25, 50, 75, 100]}
                    tick={{ fontSize: 10, fill: LLENC.textPal }}
                    stroke={LLENC.reixa} width={30} />
                  <Tooltip contentStyle={{ fontSize: 11, borderRadius: 5, borderColor: LLENC.reixa }}
                    labelFormatter={(v) => `t = ${v}`} />
                  <Legend verticalAlign="top" height={20}
                    wrapperStyle={{ fontSize: 11, paddingBottom: 2 }} />
                  {variables.map((v, i) => (
                    <Line key={v.id} type="monotone" dataKey={v.id} name={v.name}
                      stroke={v.properties?.color || colorPerIndex(i)}
                      // El segon canal: amb més de quatre variables els colors es repeteixen, i
                      // el traç les torna a separar sense inventar tons nous.
                      strokeDasharray={tracPerIndex(i) || undefined}
                      strokeWidth={2} dot={false} isAnimationActive={false} />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}

      {/* El rellotge del model. Vivia a la franja de controls de sota, que s'ha tret; sense
          això no hi ha manera de saber quant temps de model ha passat. */}
      {isSimulating && (
        <div className="absolute bottom-3 left-3 z-10 bg-white/90 border border-gray-200 rounded-md
                        px-2.5 py-1 text-xs font-mono text-gray-700 tabular-nums">
          t = {simulationTime.toFixed(1)}
        </div>
      )}

      {/* El camp per canviar el nom, damunt del node mateix. Les coordenades del model es passen
          a coordenades de pantalla amb el mateix zoom i desplaçament que fa servir el llenç. */}
      {editant && (() => {
        const v = variables.find((x) => x.id === editant.id);
        if (!v) return null;
        const desa = () => {
          const nom = editant.valor.trim();
          if (nom) updateVariable(editant.id, { name: nom });
          setEditant(null);
        };
        return (
          <input
            autoFocus
            value={editant.valor}
            onChange={(e) => setEditant({ ...editant, valor: e.target.value })}
            onBlur={desa}
            onKeyDown={(e) => {
              if (e.key === 'Enter') desa();
              if (e.key === 'Escape') setEditant(null);
            }}
            style={{
              position: 'absolute',
              left: v.x * zoom + pan.x,
              top: (v.y + 22) * zoom + pan.y,
              width: 80 * zoom,
            }}
            className="z-30 text-center text-xs font-semibold border border-primary-700 rounded-md
                       px-1 py-0.5 bg-white shadow focus:outline-none focus:ring-2 focus:ring-primary-600"
          />
        );
      })()}

      {/* Feedback Loops Panel */}
      {/* ELS BUCLES TROBATS.
          Plegat per defecte: mentre dibuixes no vols una llista tapant-te la cantonada, i quan
          la vols és per un moment. La capçalera ja diu quants n'hi ha i de quina mena, que sol
          ser tota la informació que fa falta.
          Cada bucle diu **R o B** i **per quines variables passa**: «bucle 1» tot sol no vol dir
          res, i el que s'ha d'aprendre a llegir és el recorregut. */}
      {feedbackLoops.length > 0 && (
        <div className="absolute top-3 right-3 z-10 bg-white/95 border border-gray-200
                        rounded-md shadow-md max-w-[300px]">
          <button type="button" onClick={() => setVeureBucles(!veureBucles)}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-xs font-semibold
                       text-gray-700 hover:bg-gray-50 rounded-md">
            <span>{t('ssm.loops')}</span>
            <span className="flex gap-1">
              {reforcadors > 0 && (
                <span className="px-1.5 rounded-full bg-primary-100 text-primary-800 font-bold">
                  {reforcadors}R
                </span>
              )}
              {equilibradors > 0 && (
                <span className="px-1.5 rounded-full bg-flux-100 text-flux-800 font-bold">
                  {equilibradors}B
                </span>
              )}
            </span>
            <span className="ml-auto text-gray-500">{veureBucles ? '▾' : '▸'}</span>
          </button>

          {veureBucles && (
            <div className="px-2 pb-2 space-y-1.5 max-h-64 overflow-y-auto">
              {/* Els arquetips van a dalt del tot: si el dibuix s'assembla a un, és el primer
                  que val la pena saber —abans que la llista de bucles un per un—. */}
              {arquetipsTrobats.length > 0 && (
                <div className="mb-1.5 p-2 rounded-md bg-gray-50 border border-gray-200">
                  <span className="block text-[10px] font-semibold uppercase tracking-wider text-gray-500 mb-1">
                    {t('arq.sAssembla')}
                  </span>
                  <ul className="space-y-1">
                    {arquetipsTrobats.map((a) => (
                      <li key={a.id}>
                        <Link to="/arquetips"
                          className="text-xs font-medium text-primary-700 hover:underline">
                          {t(`arq.${a.id}Nom`)}
                        </Link>
                        <span className="text-[11px] text-gray-500 ml-1.5">
                          {a.bucles.map((b) => `${b.type === 'reinforcing' ? 'R' : 'B'}${b.path.length}`).join(' + ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                  <p className="text-[10px] text-gray-500 mt-1.5 leading-snug">{t('arq.sAssemblaNota')}</p>
                </div>
              )}

              {feedbackLoops.map((loop) => {
                const esR = loop.type === 'reinforcing';
                const noms = loop.path.map((id) =>
                  variables.find((v) => v.id === id)?.name || '?');
                return (
                  <button key={loop.id} type="button"
                    onClick={() => setHighlightedLoop(highlightedLoop?.id === loop.id ? null : loop)}
                    title={esR ? t('help.reinforcingBody') : t('help.balancingBody')}
                    className={`w-full text-left p-2 text-xs rounded-md border transition-colors ${
                      esR ? 'bg-primary-50 border-primary-200 hover:bg-primary-100'
                          : 'bg-flux-50 border-flux-200 hover:bg-flux-100'
                    } ${highlightedLoop?.id === loop.id ? 'ring-2 ring-primary-600' : ''}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`inline-grid place-items-center w-5 h-5 rounded-full
                                        text-[11px] font-bold text-white flex-shrink-0
                                        ${esR ? 'bg-primary-700' : 'bg-flux-700'}`}>
                        {esR ? 'R' : 'B'}
                      </span>
                      <span className="font-semibold text-gray-800">
                        {esR ? t('help.reinforcing') : t('help.balancing')}
                      </span>
                      <span className="ml-auto text-gray-500 text-[11px]">
                        {t('ssm.loopVars', { n: loop.path.length })}
                      </span>
                    </div>
                    {/* El recorregut. Abans es repetia el primer nom al final per indicar que
                        tanca, i amb noms iguals això es llegia com una variable de més: en un
                        bucle de tres sortien quatre noms. Ara el tancament el diu el símbol i
                        el recompte, que no es poden confondre amb una variable. */}
                    <div className="text-gray-700 leading-snug">
                      {noms.join(' → ')} <span className="text-gray-500">↺</span>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
      )}

      <svg
        ref={svgRef}
        className={`w-full h-full ${getCursorStyle()}`}
        onClick={handleCanvasClick}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
      >
        <g data-contingut transform={`translate(${pan.x}, ${pan.y}) scale(${zoom})`}>
          {/* Render relationships first (behind variables) */}
          {renderRelationships()}
          
          {/* Render flow particles */}
          {simulationSettings.showParticles && renderFlowParticles()}
          
          {/* Render variables */}
          {variables.map((variable) => (
            <VariableComponent
              key={variable.id}
              variable={variable}
              currentValue={variableValues[variable.id]}
              isSimulating={isSimulating}
              showValues={simulationSettings.showValues}
              isSelected={selectedComponent?.id === variable.id}
              isLinkStart={linkStart?.id === variable.id}
              mode={mode}
              onClick={(e) => handleVariableClick(variable, e)}
              onDoubleClick={() => setEditant({ id: variable.id, valor: variable.name })}
              onMove={(deltaX, deltaY) => moveVariable(variable.id, deltaX, deltaY)}
            />
          ))}
        </g>
        
        {/* Arrow markers */}
        <defs>
          <marker
            id="arrowhead-+"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={LLENC.linia} />
          </marker>
          <marker
            id="arrowhead--"
            markerWidth="10"
            markerHeight="7"
            refX="9"
            refY="3.5"
            orient="auto"
          >
            <polygon points="0 0, 10 3.5, 0 7" fill={LLENC.linia} />
          </marker>
        </defs>
      </svg>
      
      {/* Zoom Controls */}
      <div className="absolute bottom-4 right-4 bg-white rounded-md shadow p-2 flex space-x-2">
        <button
          onClick={() => setZoom(Math.min(zoom + 0.1, 3))}
          className="px-2 py-1 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
        >
          +
        </button>
        <span className="px-2 py-1 text-sm">{Math.round(zoom * 100)}%</span>
        <button
          onClick={() => setZoom(Math.max(zoom - 0.1, 0.5))}
          className="px-2 py-1 text-sm bg-gray-100 rounded-md hover:bg-gray-200"
        >
          -
        </button>
      </div>
    </div>
  );
});

const VariableComponent = ({ variable, currentValue, isSimulating, showValues, isSelected, isLinkStart, mode, onClick, onDoubleClick, onMove }) => {
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    e.stopPropagation();
    // Allow dragging in select mode (not in other modes like link, delete, etc.)
    if (mode === 'select') {
      setIsDragging(true);
      setDragStart({ x: e.clientX, y: e.clientY });
    }
  };

  useEffect(() => {
    const handleMouseMove = (e) => {
      if (isDragging) {
        const deltaX = e.clientX - dragStart.x;
        const deltaY = e.clientY - dragStart.y;
        if (Math.abs(deltaX) > 2 || Math.abs(deltaY) > 2) {
          onMove(deltaX, deltaY);
          setDragStart({ x: e.clientX, y: e.clientY });
        }
      }
    };

    const handleMouseUp = () => {
      setIsDragging(false);
    };

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging, dragStart, onMove]);

  // Dynamic styling based on simulation
  const getVariableColor = () => {
    if (isLinkStart) return LLENC.enCurs;   // mentre s'està traçant l'enllaç
    
    // Use custom color when not simulating
    if (!isSimulating) {
      return variable.properties?.color || PER_DEFECTE;
    }
    
    // Color based on current value during simulation - more dynamic range
    const value = currentValue !== undefined ? currentValue : 50;
    const normalizedValue = Math.max(0, Math.min(1, (value - 10) / 80)); // Normalize to 0-1 based on our bounds (10-90)
    
    // Use HSL for smooth color transitions during simulation
    // Una rampa d'un sol to, del blau pàl·lid al blau fosc.
    //
    // Abans anava de vermell a verd, i això té dos problemes. El primer és que **el vermell i el
    // verd són el parell que no distingeix la gent amb daltonisme més comú**, i aquí codifiquen
    // el valor d'una variable: qui no els distingeixi no veu res del que passa. El segon és que
    // el vermell i el verd, en aquesta aplicació, volen dir «error» i «correcte»: fer-los servir
    // per a un valor alt o baix els treu el significat.
    //
    // Una magnitud que va de poc a molt es codifica amb UN to que s'enfosqueix, i així també es
    // llegeix en escala de grisos o imprès.
    // El recorregut es queda dins del tram on el text blanc de dins del node encara es llegeix:
    // amb la rampa arribant fins al 78% de claror, el punt mig no funcionava ni amb text blanc
    // ni amb text fosc. Comprovat als onze punts: el pitjor cas és 4,53:1, i entre els dos
    // extrems hi ha sis vegades de lluminositat, o sigui que el canvi es veu igual de bé.
    const lightness = 54 - normalizedValue * 30;      // 54% → 24%
    const saturation = 55 + normalizedValue * 28;     // 55% → 83%

    return `hsl(220, ${saturation}%, ${lightness}%)`;
  };

  // EL NODE NO CANVIA DE MIDA, S'OMPLE I ES BUIDA.
  //
  // El radi codificava el valor i era una mala tria per tres motius. En prémer engegar el node
  // **encollia** de 30 a 19 —el valor inicial és baix, i el mapatge començava més avall que el
  // radi de repòs—, cosa que es llegeix com «s'ha trencat alguna cosa». Comparar àrees de cercles
  // és de les coses que l'ull fa pitjor. I a sobre hi havia un pols de ±1,5 px tret d'un sinus del
  // rellotge, que és soroll sense significat.
  //
  // Ara la mida és fixa i el que canvia és **quant està ple**, com un dipòsit que s'omple i es
  // buida. És el que fa Loopy i és el que la gent llegeix sense que ningú l'hi expliqui.
  const RADI = 30;
  const nivell = Math.max(0, Math.min(1, (currentValue ?? 50) / 100));

  // Cap on va. Es guarda el valor d'abans i es compara; el llindar evita que la fletxa parpellegi
  // amb els decimals de cada fotograma.
  const anterior = useRef(currentValue);
  const [tendencia, setTendencia] = useState(0);
  useEffect(() => {
    if (currentValue === undefined) return;
    const previ = anterior.current;
    // La referència s'actualitza SEMPRE. Abans només es tocava dins de l'`if`, o sigui que la
    // primera vegada es quedava a `undefined`, la diferència donava zero i la fletxa no sortia mai.
    anterior.current = currentValue;
    if (previ === undefined) return;
    const d = currentValue - previ;
    if (Math.abs(d) > 0.02) setTendencia(Math.sign(d));
  }, [currentValue]);

  return (
    <g 
      onClick={onClick} 
      onDoubleClick={(e) => { e.stopPropagation(); if (onDoubleClick) onDoubleClick(); }}
      onMouseDown={handleMouseDown} 
      className="cursor-move"
    >
      {/* El cos del node. Mentre corre la simulació queda buit i el que l'omple és la capa de
          sota; amb la simulació aturada va ple del seu color, com sempre. */}
      <circle
        cx={variable.x + 40}
        cy={variable.y + 30}
        r={RADI}
        fill={isSimulating ? '#FFFFFF' : getVariableColor()}
        stroke={isSelected ? LLENC.variable.seleccio : (isLinkStart ? LLENC.enCurs : LLENC.linia)}
        strokeWidth={isSelected || isLinkStart ? 3 : 2}
        className="drop-shadow-sm"
      />

      {/* EL NIVELL. Un rectangle que puja i baixa, retallat a la forma del cercle. La cara
          superior és la línia de flotació: es veu pujar i baixar sense haver de comparar res. */}
      {isSimulating && (
        <>
          <defs>
            <clipPath id={`niv-${variable.id}`}>
              <circle cx={variable.x + 40} cy={variable.y + 30} r={RADI - 1} />
            </clipPath>
          </defs>
          <rect
            x={variable.x + 40 - RADI}
            y={variable.y + 30 + RADI - 2 * RADI * nivell}
            width={2 * RADI}
            height={2 * RADI * nivell}
            fill={getVariableColor()}
            clipPath={`url(#niv-${variable.id})`}
          />
        </>
      )}
      
      {/* Variable name */}
      <text
        x={variable.x + 40}
        y={variable.y + (isSimulating ? 25 : 35)}
        textAnchor="middle"
        {...(isSimulating
          ? { fill: LLENC.text, stroke: '#FFFFFF', strokeWidth: 3, paintOrder: 'stroke' }
          : { fill: '#FFFFFF' })}
        fontSize="11"
        fontWeight="600"
        className="pointer-events-none select-none"
      >
        {variable.name}
      </text>
      
      {/* Current value display during simulation */}
      {isSimulating && showValues && currentValue !== undefined && (
        <text
          x={variable.x + 40}
          y={variable.y + 40}
          textAnchor="middle"
          fill={LLENC.text}
          stroke="#FFFFFF"
          strokeWidth="3"
          paintOrder="stroke"
          fontSize="11"
          fontWeight="700"
          className="pointer-events-none select-none"
        >
          {Math.round(currentValue)}
        </text>
      )}

      {/* Cap on va ARA. El número diu on és; això diu si puja o baixa, que és la meitat de la
          pregunta i no es pot deduir mirant una foto. */}
      {isSimulating && tendencia !== 0 && (
        <text
          x={variable.x + 40 + 20}
          y={variable.y + 44}
          textAnchor="middle"
          fill={tendencia > 0 ? LLENC.variable.vora : LLENC.enCurs}
          stroke="#FFFFFF" strokeWidth="3" paintOrder="stroke"
          fontSize="13" fontWeight="700"
          className="pointer-events-none select-none"
        >
          {tendencia > 0 ? '▲' : '▼'}
        </text>
      )}
    </g>
  );
};

export default CausalLoopCanvas;